/**
 * The room as it stands: tables where they are, not in label order.
 *
 * A host thinks "the window four-top", not "table 14", and a grid sorted by
 * label makes them translate. So this draws a floor, and on it each table the
 * size of what it seats, round where it is round, with its chairs around it —
 * a room somebody recognises at a glance from the door, which a row of
 * identical squares was not.
 *
 * Positions are stored per table in plan units and drawn scaled to the screen,
 * so one layout serves a tablet and a till alike. Arranging is dragging: pick
 * a table up, put it down, and it is saved where it lands, snapped to a small
 * grid so rows line up without anybody trying.
 */

import {
  useLayoutEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
  type RefObject,
} from 'react';
import { useMoveTable, type FloorTable } from '@restaurant/api-client';
import { ActionError, Button } from '@restaurant/ui';

import {
  CHAIR_ROOM,
  MAX_SCALE,
  MIN_PLAN_WIDTH,
  MIN_SCALE,
  SNAP,
  positionsOf,
  spotSize,
  tableSize,
  type Point,
} from './room-layout-state';

/**
 * The room's width, and the height of whatever scrolls it: the plan is fitted
 * to both, so a room that fits the screen is shown whole, not below the fold.
 */
function useRoomBox(): [RefObject<HTMLDivElement | null>, number, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const scroller = element.parentElement;
    const measure = () =>
      setBox({
        width: element.clientWidth,
        // Room left in the scrolling area below the top of the floor.
        height: scroller
          ? scroller.clientHeight - (element.offsetTop - scroller.offsetTop) - 24
          : 0,
      });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    if (scroller) observer.observe(scroller);
    return () => observer.disconnect();
  }, []);
  return [ref, box.width, box.height];
}

/** The floor the tables need, with a margin so none sits on the edge. */
function planSize(tables: FloorTable[], positions: Map<string, Point>) {
  let width = MIN_PLAN_WIDTH;
  let height = 0;
  for (const table of tables) {
    const at = positions.get(table.id) ?? { x: 0, y: 0 };
    const spot = spotSize(table);
    width = Math.max(width, at.x + spot.w + 20);
    height = Math.max(height, at.y + spot.h + 20);
  }
  return { width, height };
}

function scaleFor(
  width: number,
  height: number,
  plan: { width: number; height: number },
): number {
  if (width <= 0) return 1;
  const fit =
    height > 120 ? Math.min(width / plan.width, height / plan.height) : width / plan.width;
  return Math.min(MAX_SCALE, Math.max(fit, MIN_SCALE));
}

/**
 * Where each chair goes around a table, in plan units from the spot's corner.
 *
 * Long tables take their chairs along both long sides; square ones spread
 * them over all four; round ones evenly round the edge. What the drawing
 * says is the furniture: `seats`, not the joined-up count of pushed tables.
 */
function chairs(table: FloorTable): { x: number; y: number; w: number; h: number }[] {
  const { w, h, round } = tableSize(table);
  const n = Math.max(0, Math.min(table.seats, 16));
  const cx = CHAIR_ROOM + w / 2;
  const cy = CHAIR_ROOM + h / 2;
  const size = 12;

  if (round) {
    const r = w / 2 + CHAIR_ROOM / 2;
    return Array.from({ length: n }, (_, i) => {
      const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
      return {
        x: cx + Math.cos(angle) * r - size / 2,
        y: cy + Math.sin(angle) * r - size / 2,
        w: size,
        h: size,
      };
    });
  }

  // Sides in the order chairs are handed out: top and bottom first, then
  // the ends once the long sides are full.
  const along = (count: number, length: number) =>
    Array.from({ length: count }, (_, i) => ((i + 0.5) * length) / count);
  const square = Math.abs(w - h) < 10;
  let top = 0;
  let bottom = 0;
  let left = 0;
  let right = 0;
  for (let i = 0; i < n; i++) {
    const side = square ? i % 4 : i % 2;
    if (side === 0) top++;
    else if (side === 1) bottom++;
    else if (side === 2) left++;
    else right++;
  }
  const long = 18;
  const deep = 7;
  return [
    ...along(top, w).map((x) => ({
      x: CHAIR_ROOM + x - long / 2,
      y: CHAIR_ROOM - deep - 3,
      w: long,
      h: deep,
    })),
    ...along(bottom, w).map((x) => ({
      x: CHAIR_ROOM + x - long / 2,
      y: CHAIR_ROOM + h + 3,
      w: long,
      h: deep,
    })),
    ...along(left, h).map((y) => ({
      x: CHAIR_ROOM - deep - 3,
      y: CHAIR_ROOM + y - long / 2,
      w: deep,
      h: long,
    })),
    ...along(right, h).map((y) => ({
      x: CHAIR_ROOM + w + 3,
      y: CHAIR_ROOM + y - long / 2,
      w: deep,
      h: long,
    })),
  ];
}

/** One table on the floor: its chairs, and whatever `children` draw as the top. */
function Spot({
  table,
  at,
  scale,
  className = '',
  children,
  ...rest
}: {
  table: FloorTable;
  at: Point;
  scale: number;
  className?: string;
  children: ReactNode;
} & HTMLAttributes<HTMLDivElement>) {
  const spot = spotSize(table);
  const { w, h, round } = tableSize(table);
  return (
    <div
      className={`rm__spot ${className}`.trim()}
      style={{
        left: at.x * scale,
        top: at.y * scale,
        width: spot.w * scale,
        height: spot.h * scale,
      }}
      {...rest}
    >
      {chairs(table).map((chair, index) => (
        <span
          key={index}
          className={`rm__chair${round ? ' is-round' : ''}`}
          aria-hidden="true"
          style={{
            left: chair.x * scale,
            top: chair.y * scale,
            width: chair.w * scale,
            height: chair.h * scale,
          }}
        />
      ))}
      <div
        className={`rm__top${round ? ' is-round' : ''}`}
        style={{
          left: CHAIR_ROOM * scale,
          top: CHAIR_ROOM * scale,
          width: w * scale,
          height: h * scale,
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** The room drawn to scale, each table's top rendered by `tile`. Read-only. */
export function RoomMap({
  tables,
  tile,
}: {
  tables: FloorTable[];
  tile: (table: FloorTable, round: boolean) => ReactNode;
}) {
  const positions = positionsOf(tables);
  const [ref, width, height] = useRoomBox();
  const size = planSize(tables, positions);
  const scale = scaleFor(width, height, size);

  return (
    <div ref={ref} className="rm__scroll">
      <div
        className="rm"
        style={{ width: size.width * scale, height: Math.max(size.height * scale, 220) }}
      >
        {width > 0 &&
          tables.map((table) => (
            <Spot
              key={table.id}
              table={table}
              at={positions.get(table.id) ?? { x: 0, y: 0 }}
              scale={scale}
            >
              {tile(table, tableSize(table).round)}
            </Spot>
          ))}
      </div>
    </div>
  );
}

/** Drag tables into place. Each drop is saved as it lands. */
export function ArrangeRoom({
  tables,
  start,
  onDone,
}: {
  tables: FloorTable[];
  /** Positions just saved, when the room was laid out a moment ago. */
  start?: Map<string, Point> | null | undefined;
  onDone: () => void;
}) {
  const move = useMoveTable();
  const [positions, setPositions] = useState(() => start ?? positionsOf(tables));
  const [ref, width, height] = useRoomBox();
  const [drag, setDrag] = useState<{ id: string; from: Point; pointer: Point } | null>(null);

  // Room to drag into below and to the right of the furthest table.
  const size = planSize(tables, positions);
  const scale = scaleFor(width, height, { width: size.width + 160, height: size.height + 200 });

  function place(id: string, at: Point) {
    const snapped = {
      x: Math.max(0, Math.round(at.x / SNAP) * SNAP),
      y: Math.max(0, Math.round(at.y / SNAP) * SNAP),
    };
    setPositions((all) => new Map(all).set(id, snapped));
    move.mutate({ tableId: id, posX: snapped.x, posY: snapped.y });
  }

  return (
    <div className="rm__arrange">
      <div className="rm__arrange-head">
        <p className="rm__arrange-note">
          Drag each table to where it stands. Each one is saved as you let go.
        </p>
        <Button variant="primary" onClick={onDone}>
          Done
        </Button>
      </div>
      <ActionError
        error={move.error}
        action="Arranging tables"
        title="That table did not move"
      />
      <div ref={ref} className="rm__scroll">
        <div
          className="rm rm--arranging"
          style={{
            width: (size.width + 160) * scale,
            height: Math.max((size.height + 200) * scale, 320),
          }}
        >
          {width > 0 &&
            tables.map((table) => {
              const at = positions.get(table.id) ?? { x: 0, y: 0 };
              const round = tableSize(table).round;
              return (
                <Spot
                  key={table.id}
                  table={table}
                  at={at}
                  scale={scale}
                  role="button"
                  tabIndex={0}
                  aria-label={`Table ${table.label}. Drag to move, or use the arrow keys.`}
                  className={`rm__drag${drag?.id === table.id ? ' is-dragging' : ''}`}
                  onPointerDown={(event) => {
                    event.currentTarget.setPointerCapture(event.pointerId);
                    setDrag({
                      id: table.id,
                      from: at,
                      pointer: { x: event.clientX, y: event.clientY },
                    });
                  }}
                  onPointerMove={(event) => {
                    if (drag?.id !== table.id) return;
                    const dx = (event.clientX - drag.pointer.x) / scale;
                    const dy = (event.clientY - drag.pointer.y) / scale;
                    setPositions((all) =>
                      new Map(all).set(table.id, {
                        x: Math.max(0, drag.from.x + dx),
                        y: Math.max(0, drag.from.y + dy),
                      }),
                    );
                  }}
                  onPointerUp={() => {
                    if (drag?.id !== table.id) return;
                    setDrag(null);
                    place(table.id, positions.get(table.id) ?? at);
                  }}
                  onPointerCancel={() => {
                    if (drag?.id !== table.id) return;
                    setDrag(null);
                    place(table.id, positions.get(table.id) ?? at);
                  }}
                  onKeyDown={(event) => {
                    // Arrow keys move a table a step, for anybody not dragging.
                    const step = event.shiftKey ? SNAP * 5 : SNAP;
                    const by: Record<string, Point> = {
                      ArrowLeft: { x: -step, y: 0 },
                      ArrowRight: { x: step, y: 0 },
                      ArrowUp: { x: 0, y: -step },
                      ArrowDown: { x: 0, y: step },
                    };
                    const delta = by[event.key];
                    if (!delta) return;
                    event.preventDefault();
                    place(table.id, { x: at.x + delta.x, y: at.y + delta.y });
                  }}
                >
                  <span className={`rm__face${round ? ' is-round' : ''}`}>
                    <span className="rm__label">{table.label}</span>
                  </span>
                </Spot>
              );
            })}
        </div>
      </div>
    </div>
  );
}
